import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Breadcrumb, Select } from "antd";
import { Link } from "react-router-dom";
import styled from "styled-components";

import useGet from "../../hooks/common/useGet";
import { Wrapper } from "../styled/Styled";
import { Table } from "../common/Table";
import { TableFilter } from "../common/TableFilter";
import { ExportButton } from "../common/Buttons";
import { GenericBarChart, GenericLineChart } from "../charts/GenericBarChart";
import { reportHooks } from "../../hooks/handlers/useReportHandler";
import { reportApis } from "../../hooks/handlers/useReportApiHandler";
import { TGeneralHookParams } from "../../types/common";
import {
  downloadThroughAnchorLink,
  returnObject,
  beautify,
} from "../../utils/helpers";
import { BASE_API_URL } from "../../config";

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: end;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ChartsWrapper = styled.div`
  display: flex;
  gap: 20px;
  border-radius: 10px;
  padding: 16px;
  background-color: #fff;
  flex-wrap: wrap;

  & > * {
    flex: 1 1 45%;
    min-width: 300px;
  }

  @media (max-width: 1024px) {
    & > * {
      flex: 1 1 100%;
    }
  }
`;

const EmptyState = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #8c8c8c;
  background: #fafafa;
  border-radius: 10px;
  border: 1px dashed #d9d9d9;
`;

const REPORT_TYPE_OPTIONS = [
  { label: "Date Range", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Yearly", value: "yearly" },
];

const MONTH_OPTIONS = [
  { label: "January", value: "1" },
  { label: "February", value: "2" },
  { label: "March", value: "3" },
  { label: "April", value: "4" },
  { label: "May", value: "5" },
  { label: "June", value: "6" },
  { label: "July", value: "7" },
  { label: "August", value: "8" },
  { label: "September", value: "9" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

type ReportType = "daily" | "weekly" | "monthly" | "yearly";

type ApiResolver = string | ((reportType: ReportType) => string);

const resolveApi = (resolver: ApiResolver, reportType: ReportType): string => {
  return typeof resolver === "function" ? resolver(reportType) : resolver;
};

const ReportLayoutHandler = (props: any) => {
  const {
    source_id,

    api,
    title,
    // fullWidth = true,
    report_params,
    showGraphs = true,
    showTable = true,
  } = props;

  let { id: source } = useParams<keyof TGeneralHookParams>();
  if (source_id) source = source_id;

  const [searchParams] = useSearchParams();

  const hookResult =
    source && reportHooks[source]
      ? reportHooks[source]({ ...report_params })
      : {};

  const {
    tableData,
    tableColumns,
    reportTitle,

    graphData: graphDataTransform,
    externalFilters = [],
    initialFilters = {},

    customApi,

    customGraphApi,
    exportData,
    reportTypeOptions,
    totalTypeOptions,
    showReportTypeFilter = true,
    showTotalTypeFilter = false,
    showMonthFilter = true,
    showYearFilter = true,
    showMerchantFilter = false,
    merchantFilterParamKey = "merchant_number",
    showPaymentItemFilter = false,
    paymentItemFilterParamKey = "item_name",
    defaultReportType = "monthly",
    defaultTotalType = "total_amount",
  } = hookResult;

  const [reportType, setReportType] = useState<ReportType>(defaultReportType);

  const [totalType, setTotalType] = useState<"total_amount" | "total_count">(
    defaultTotalType || "total_amount",
  );

  const [selectedMonth, setSelectedMonth] = useState<string | undefined>(
    (new Date().getMonth() + 1).toString(),
  );

  const [selectedYear, setSelectedYear] = useState<string | undefined>(
    new Date().getFullYear().toString(),
  );

  const [selectedMerchant, setSelectedMerchant] = useState<
    string | undefined
  >();

  const [merchantOptions, setMerchantOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const seenMerchantsRef = useRef<Set<string>>(new Set());

  const [selectedPaymentItem, setSelectedPaymentItem] = useState<
    string | undefined
  >();

  const [paymentItemOptions, setPaymentItemOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const seenPaymentItemsRef = useRef<Set<string>>(new Set());

  const [isDownloadingFiltered, setIsDownloadingFiltered] = useState(false);

  const [filters, setFilters] = useState<Record<string, any>>(() => ({
    ...Object.fromEntries(searchParams.entries()),
    ...initialFilters,
    start_date: null,
    end_date: new Date().toISOString().split("T")[0],
  }));

  const {
    loadData: loadTableData,
    data: tablePaginatedData,
    loading: tableLoading,
  } = useGet();

  const {
    loadData: loadGraphData,
    data: rawGraphData,
    loading: graphLoading,
  } = useGet();

  const getTableApi = (rt: ReportType): string | null => {
    if (api) return api;
    if (customApi) return resolveApi(customApi as ApiResolver, rt);
    if (source && reportApis[source]) return reportApis[source];
    return null;
  };

  const getGraphApi = (rt: ReportType): string | null => {
    if (customGraphApi) return resolveApi(customGraphApi as ApiResolver, rt);
    const base = getTableApi(rt);
    return base ? `${base}graphic-data/` : null;
  };

  const buildParams = (
    rt: ReportType,
    yr: string | undefined,
    mo: string | undefined,
    f: Record<string, any>,
    merchant: string | undefined,
    paymentItem: string | undefined,
    includePageSize = true,
  ) => ({
    ...f,
    ...(includePageSize && { page_size: 10 }),
    ...(rt !== "yearly" && { year_value: yr }),
    ...(rt === "weekly" && { month_value: mo }),
    ...(merchant && { [merchantFilterParamKey]: merchant }),
    ...(paymentItem && { [paymentItemFilterParamKey]: paymentItem }),
  });

  useEffect(() => {
    const tableApi = getTableApi(reportType);
    if (!tableApi) return;

    loadTableData({
      api: tableApi,
      params: buildParams(
        reportType,
        selectedYear,
        selectedMonth,
        filters,
        selectedMerchant,
        selectedPaymentItem,
      ),
    });
  }, [
    reportType,
    filters,
    selectedYear,
    selectedMonth,
    selectedMerchant,
    selectedPaymentItem,
    source,
  ]);

  useEffect(() => {
    if (!showGraphs) return;

    const graphApi = getGraphApi(reportType);
    if (!graphApi) return;

    loadGraphData({
      api: graphApi,
      params: buildParams(
        reportType,
        selectedYear,
        selectedMonth,
        filters,
        selectedMerchant,
        selectedPaymentItem,
        false, // graph endpoint usually doesn't need page_size
      ),
    });
  }, [
    reportType,
    filters,
    selectedYear,
    selectedMonth,
    selectedMerchant,
    selectedPaymentItem,
    showGraphs,
    source,
  ]);

  useEffect(() => {
    const newParams = Object.fromEntries(searchParams.entries());
    setFilters((prev) => {
      const next = { ...prev, ...newParams };
      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
    });
  }, [searchParams]);

  useEffect(() => {
    if (!showMerchantFilter) return;
    const results: any[] =
      tablePaginatedData?.results ||
      (Array.isArray(tablePaginatedData) ? tablePaginatedData : []);
    if (!results.length) return;

    const newOptions: Array<{ label: string; value: string }> = [];
    results.forEach((item: any) => {
      const merchantNumber = item?.merchant_number;
      if (merchantNumber && !seenMerchantsRef.current.has(merchantNumber)) {
        seenMerchantsRef.current.add(merchantNumber);
        newOptions.push({
          label: item?.business_name
            ? `${item.business_name} (${merchantNumber})`
            : merchantNumber,
          value: merchantNumber,
        });
      }
    });

    if (newOptions.length) {
      setMerchantOptions((prev) => [...prev, ...newOptions]);
    }
  }, [tablePaginatedData, showMerchantFilter]);

  useEffect(() => {
    if (!showPaymentItemFilter) return;
    const results: any[] =
      tablePaginatedData?.results ||
      (Array.isArray(tablePaginatedData) ? tablePaginatedData : []);
    if (!results.length) return;

    const newOptions: Array<{ label: string; value: string }> = [];
    results.forEach((item: any) => {
      const itemName = item?.item_name;
      if (itemName && !seenPaymentItemsRef.current.has(itemName)) {
        seenPaymentItemsRef.current.add(itemName);
        newOptions.push({ label: itemName, value: itemName });
      }
    });

    if (newOptions.length) {
      setPaymentItemOptions((prev) => [...prev, ...newOptions]);
    }
  }, [tablePaginatedData, showPaymentItemFilter]);

  const graphResults = useMemo(() => {
    if (!rawGraphData) return [];
    return Array.isArray(rawGraphData)
      ? rawGraphData
      : rawGraphData?.results || [];
  }, [rawGraphData]);

  /** Transform graph results into { xLabels, series, yLabel? }. */
  const chartData = useMemo(() => {
    if (!showGraphs || !graphResults.length) {
      return { xLabels: [], series: [], yLabel: "Value" };
    }
    if (graphDataTransform) {
      return graphDataTransform(graphResults, reportType, totalType);
    }
    return { xLabels: [], series: [], yLabel: "Value" };
  }, [graphResults, reportType, totalType, graphDataTransform, showGraphs]);

  /** Apply the hook's tableData transformer to the paginated results. */
  const transformedTableData = useMemo(() => {
    if (!tableData) return [];
    const results =
      tablePaginatedData?.results ||
      (Array.isArray(tablePaginatedData) ? tablePaginatedData : []);
    return tableData(results, reportType);
  }, [tablePaginatedData, reportType, tableData]);

  /** Apply the hook's tableColumns factory for the current report type. */
  const transformedTableColumns = useMemo(() => {
    if (!tableColumns) return [];
    return tableColumns(reportType);
  }, [reportType, tableColumns]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => {
      const year = (currentYear - i).toString();
      return { label: year, value: year };
    });
  }, []);

  const onFilterChange = (key: string, value: string | number) => {
    const updated = { ...filters, page: 1 };
    if (value === "" || value === null || value === undefined) {
      delete updated[key];
    } else {
      updated[key] = value;
    }
    setFilters(updated);
  };

  const handleTableFilterChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const handleReportTypeChange = (value: ReportType) => {
    setReportType(value);
    setSelectedYear(new Date().getFullYear().toString());
    // Reset merchant filter when switching report types
    setSelectedMerchant(undefined);
    setMerchantOptions([]);
    seenMerchantsRef.current = new Set();
    // Reset payment item filter when switching report types
    setSelectedPaymentItem(undefined);
    setPaymentItemOptions([]);
    seenPaymentItemsRef.current = new Set();
    // Reset filters to initial state when switching report types
    setFilters({
      ...initialFilters,
      start_date: null,
      end_date: new Date().toISOString().split("T")[0],
    });
  };

  /** Trigger an export download. */
  const handleDownload = (filtered: boolean = false) => {
    if (!exportData) return;
    if (filtered) setIsDownloadingFiltered(true);

    const endpoint = exportData.getEndpoint
      ? exportData.getEndpoint(reportType)
      : exportData.api;

    downloadThroughAnchorLink(
      `${BASE_API_URL}${endpoint}`,
      exportData.fileName || `${source}-${reportType}-report.xlsx`,
      returnObject(filtered, {
        ...filters,
        year_value: selectedYear,
        month_value: selectedMonth,
        report_type: reportType,
      }),
    ).finally(() => {
      if (filtered) setIsDownloadingFiltered(false);
    });
  };

  const getReportTitle = () => {
    if (title) return title;
    if (reportTitle) return reportTitle;

    const typeLabel =
      reportType === "daily"
        ? "Daily"
        : reportType === "weekly"
          ? "Weekly"
          : reportType === "yearly"
            ? "Yearly"
            : "Monthly";

    return `${typeLabel} ${beautify(source)} Report`;
  };

  const hasNoData = !graphLoading && graphResults.length === 0;
  const finalReportTypeOptions = reportTypeOptions || REPORT_TYPE_OPTIONS;

  return (
    <Wrapper>
      <div className="p-4 flexColumn gap20">
        {/* Breadcrumb */}
        <HeaderWrapper>
          <Breadcrumb
            items={[
              { title: <Link to="/">Home</Link> },
              { title: getReportTitle() },
            ]}
          />
        </HeaderWrapper>

        {/* Graphs section */}
        {showGraphs && (
          <div className="whiteBg p-4" style={{ borderRadius: 10 }}>
            <FilterRow style={{ marginBottom: "30px" }}>
              {/* Report Type selector */}
              {showReportTypeFilter && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                  }}
                >
                  <label style={{ fontSize: "14px" }}>Report Type</label>
                  <Select
                    options={finalReportTypeOptions}
                    value={reportType}
                    onChange={handleReportTypeChange}
                    style={{ width: 140 }}
                  />
                </div>
              )}

              {/* Date range filter – shown only for "daily" report type */}
              {reportType === "daily" && externalFilters.length > 0 && (
                <div style={{ display: "flex", gap: 30 }}>
                  {externalFilters.map((row: any) => (
                    <TableFilter
                      key={row.id}
                      name={row.id}
                      onChange={onFilterChange}
                      filters={filters}
                      updateFilterValues={(obj: any) =>
                        setFilters((prev: any) => ({ ...prev, ...obj }))
                      }
                      {...row}
                      showHeaders={true}
                    />
                  ))}
                </div>
              )}

              {/* Month filter – shown only for "weekly" report type */}
              {reportType === "weekly" && showMonthFilter && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                  }}
                >
                  <label style={{ fontSize: "14px" }}>Filter by Month</label>
                  <Select
                    placeholder="Select month"
                    options={MONTH_OPTIONS}
                    value={selectedMonth}
                    onChange={(value) => setSelectedMonth(value)}
                    allowClear
                    style={{ width: 140 }}
                    loading={graphLoading}
                  />
                </div>
              )}

              {/* Year filter – shown for "monthly" and "weekly" report types */}
              {(reportType === "monthly" || reportType === "weekly") &&
                showYearFilter && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px",
                    }}
                  >
                    <label style={{ fontSize: "14px" }}>Filter by Year</label>
                    <Select
                      placeholder="All years"
                      options={yearOptions}
                      value={selectedYear}
                      onChange={(value) => setSelectedYear(value)}
                      allowClear
                      style={{ width: 140 }}
                      loading={tableLoading}
                    />
                  </div>
                )}

              {/* Total type selector – only shown when the hook opts in */}
              {showTotalTypeFilter && totalTypeOptions && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                  }}
                >
                  <label style={{ fontSize: "14px" }}>Total Type</label>
                  <Select
                    options={totalTypeOptions}
                    value={totalType}
                    onChange={(value) => setTotalType(value)}
                    style={{ width: 160 }}
                  />
                </div>
              )}

              {/* Merchant filter – only shown when the hook opts in */}
              {showMerchantFilter && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                  }}
                >
                  <label style={{ fontSize: "14px" }}>Filter by Merchant</label>
                  <Select
                    placeholder="All merchants"
                    options={merchantOptions}
                    value={selectedMerchant}
                    onChange={(value) => setSelectedMerchant(value)}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={tableLoading}
                    style={{ width: 280 }}
                  />
                </div>
              )}

              {/* Payment item filter – only shown when the hook opts in */}
              {showPaymentItemFilter && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                  }}
                >
                  <label style={{ fontSize: "14px" }}>
                    Filter by Payment Item
                  </label>
                  <Select
                    placeholder="All payment items"
                    options={paymentItemOptions}
                    value={selectedPaymentItem}
                    onChange={(value) => setSelectedPaymentItem(value)}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={tableLoading}
                    style={{ width: 220 }}
                  />
                </div>
              )}
            </FilterRow>

            {/* Charts */}
            {hasNoData ? (
              <EmptyState>
                No data available for the selected filters
              </EmptyState>
            ) : (
              <ChartsWrapper>
                <GenericBarChart
                  data={chartData.series}
                  title={getReportTitle()}
                  xLabels={chartData.xLabels}
                  yLabel={chartData.yLabel || "Value"}
                  loading={graphLoading}
                  height={380}
                />
                <GenericLineChart
                  data={chartData.series}
                  title={`${getReportTitle()} Trend`}
                  xLabels={chartData.xLabels}
                  yLabel={chartData.yLabel || "Value"}
                  loading={graphLoading}
                  height={380}
                />
              </ChartsWrapper>
            )}
          </div>
        )}

        {/* Table */}
        {showTable && (
          <Table
            loading={tableLoading}
            count={tablePaginatedData?.count || 0}
            tableTitle={getReportTitle()}
            tableColumns={transformedTableColumns}
            tableData={transformedTableData}
            filters={filters}
            onFilterChange={handleTableFilterChange}
            noEmptyText={false}
            tableHeaderItem={
              exportData && (
                <div className="flexRow gap10">
                  <ExportButton
                    text={exportData.buttonText || "Export"}
                    onClick={() => handleDownload(true)}
                    isDownloading={isDownloadingFiltered}
                  />
                </div>
              )
            }
          />
        )}
      </div>
    </Wrapper>
  );
};

export default ReportLayoutHandler;
